/**
 * ArtShowcase.jsx
 *
 * Single, large looping media panel — a GIF or short video that plays
 * continuously. Media-loading logic (autoplay fallback, tap-to-play for
 * webviews that block autoplay, broken-link fallback) is unchanged from
 * the original build; only the surrounding chrome was redesigned.
 *
 * ── To go live ──────────────────────────────────────────────────────
 * Paste your link into ART_MEDIA_URL below. That's the only change needed.
 *   - Ends in .gif / .png / .jpg / .webp  → rendered as an <img>
 *   - Ends in .mp4 / .webm / .ogg         → rendered as an autoplaying, looping, muted <video>
 */

import React, { useEffect, useRef, useState } from 'react';
import Panel from './Panel';
import GlowText from './GlowText';

// ── Paste your GIF / video link here ──
const ART_MEDIA_URL = 'https://res.cloudinary.com/pz3yhkrd/video/upload/v1783148711/video_2026-07-04_12-34-47_gnzsne.mp4';

const isVideo = (url) => /\.(mp4|webm|ogg)(\?.*)?$/i.test(url);

export default function ArtShowcase() {
  const [failed, setFailed]   = useState(false);
  const [needsTap, setNeedsTap] = useState(false);
  const videoRef = useRef(null);
  const hasMedia = Boolean(ART_MEDIA_URL) && !failed;

  useEffect(() => {
    if (!hasMedia || !isVideo(ART_MEDIA_URL)) return;
    const v = videoRef.current;
    if (!v) return;
    setNeedsTap(false);
    const p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => setNeedsTap(true));
    }
  }, [hasMedia]);

  const manualPlay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.play().then(() => setNeedsTap(false)).catch(() => setNeedsTap(true));
  };

  return (
    <Panel title="HEXA ARTS // BROADCAST FEED">
      {hasMedia ? (
        <div className="flex justify-center">
          {isVideo(ART_MEDIA_URL) ? (
            <div
              className="mech-panel mech-corners relative w-full max-w-2xl overflow-hidden"
              style={{ padding: 6 }}
            >
              <video
                ref={videoRef}
                src={ART_MEDIA_URL}
                autoPlay
                loop
                muted
                playsInline
                webkit-playsinline="true"
                preload="auto"
                disablePictureInPicture
                onError={() => setFailed(true)}
                onCanPlay={() => {
                  const v = videoRef.current;
                  if (v && v.paused) {
                    const p = v.play();
                    if (p && typeof p.catch === 'function') p.catch(() => setNeedsTap(true));
                  }
                }}
                className="block w-full h-auto max-h-[70vh] object-contain"
              />
              {needsTap && (
                <button
                  type="button"
                  onClick={manualPlay}
                  aria-label="Play video"
                  className="absolute inset-0 flex items-center justify-center"
                  style={{ background: 'rgba(3,6,13,0.55)', cursor: 'pointer' }}
                >
                  <span className="btn-robotic btn-robotic-cyan px-6 py-3 text-sm">TAP TO PLAY</span>
                </button>
              )}
            </div>
          ) : (
            <div className="mech-panel mech-corners w-full max-w-2xl overflow-hidden" style={{ padding: 6 }}>
              <img
                src={ART_MEDIA_URL}
                alt="HEXABOTS art"
                onError={() => setFailed(true)}
                className="w-full block"
              />
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-16 px-4 space-y-4">
          <GlowText as="div" text="AWAITING SIGNAL" size={20} variant="violet" />
          <p className="font-mono-hud text-dim text-fluid-md">
            Set <code>ART_MEDIA_URL</code> in{' '}
            <code>src/components/ArtShowcase.jsx</code> to broadcast your GIF
            or video here.
          </p>
        </div>
      )}
    </Panel>
  );
}
